import { Component } from '@theme/component';
import { preventDefault } from '@theme/utilities';

const OVERFLOW_THRESHOLD = 1;
const DRAG_THRESHOLD = 4;

/**
 * Icon bar slider: row of group items that becomes a swipeable/draggable
 * carousel when the items overflow the available width.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} scroller
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
class IconBarComponent extends Component {
  requiredRefs = ['scroller'];

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {MutationObserver | null} */
  #mutationObserver = null;

  connectedCallback() {
    super.connectedCallback();

    const { scroller } = this.refs;

    scroller.addEventListener('scroll', this.#handleScroll, { passive: true });
    scroller.addEventListener('pointerdown', this.#handlePointerDown);

    this.#resizeObserver = new ResizeObserver(() => {
      this.#updateOverflowState();
    });
    this.#resizeObserver.observe(scroller);

    this.#mutationObserver = new MutationObserver(() => {
      this.#updateOverflowState();
    });
    this.#mutationObserver.observe(scroller, { childList: true, subtree: true });

    this.#updateOverflowState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const { scroller } = this.refs;

    scroller?.removeEventListener('scroll', this.#handleScroll);
    scroller?.removeEventListener('pointerdown', this.#handlePointerDown);

    this.#resizeObserver?.disconnect();
    this.#mutationObserver?.disconnect();
  }

  previous() {
    this.#scrollBy(-this.#itemStride);
  }

  next() {
    this.#scrollBy(this.#itemStride);
  }

  /**
   * @param {number} amount
   */
  #scrollBy(amount) {
    const rtl = getComputedStyle(this).direction === 'rtl';
    this.refs.scroller.scrollBy({
      left: rtl ? -amount : amount,
      behavior: 'smooth',
    });
  }

  /**
   * @returns {boolean}
   */
  #isMobileAutoscroll() {
    return this.hasAttribute('data-mobile-autoscroll') && window.matchMedia('(max-width: 749px)').matches;
  }

  /**
   * @returns {number}
   */
  get #itemStride() {
    const { scroller } = this.refs;
    const item = /** @type {HTMLElement | null} */ (
      scroller.querySelector('.marquee__repeated-items > *') || scroller.firstElementChild
    );
    if (!item) return Math.round(scroller.clientWidth * 0.6);

    const gapSource = item.parentElement || scroller;
    const gap = Number.parseFloat(getComputedStyle(gapSource).columnGap || getComputedStyle(gapSource).gap) || 0;
    return Math.round(item.getBoundingClientRect().width + gap);
  }

  #handleScroll = () => {
    this.#updateArrowState();
  };

  #updateOverflowState() {
    const { scroller } = this.refs;

    if (this.#isMobileAutoscroll()) {
      this.removeAttribute('data-overflow');
      return;
    }

    const itemsRow = scroller.querySelector('.marquee__repeated-items') || scroller;
    const children = Array.from(itemsRow.children).filter((child) => child instanceof HTMLElement);

    if (children.length === 0) {
      this.removeAttribute('data-overflow');
      return;
    }

    const gap = Number.parseFloat(getComputedStyle(itemsRow).columnGap || getComputedStyle(itemsRow).gap) || 0;
    const itemsWidth =
      children.reduce((sum, child) => sum + child.getBoundingClientRect().width, 0) +
      gap * Math.max(0, children.length - 1);
    const overflowing = itemsWidth - scroller.clientWidth > OVERFLOW_THRESHOLD;

    this.toggleAttribute('data-overflow', overflowing);
    this.#updateArrowState();
  }

  #updateArrowState() {
    const { scroller, previous, next } = this.refs;
    if (!(previous instanceof HTMLButtonElement) || !(next instanceof HTMLButtonElement)) return;

    const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    const scrollPosition = Math.abs(scroller.scrollLeft);

    previous.disabled = scrollPosition <= OVERFLOW_THRESHOLD;
    next.disabled = scrollPosition >= maxScroll - OVERFLOW_THRESHOLD;
  }

  /**
   * Desktop drag-to-scroll. Touch swipe uses native overflow scrolling.
   *
   * @param {PointerEvent} event
   */
  #handlePointerDown = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    if (this.#isMobileAutoscroll()) return;
    if (!this.hasAttribute('data-overflow')) return;
    if (event.target instanceof Element && event.target.closest('button, a')) return;

    event.preventDefault();

    const { scroller } = this.refs;
    const startX = event.clientX;
    const startScrollLeft = scroller.scrollLeft;
    const controller = new AbortController();
    const { signal } = controller;
    let moved = false;

    /**
     * @param {PointerEvent} moveEvent
     */
    const onPointerMove = (moveEvent) => {
      const delta = moveEvent.clientX - startX;

      if (!moved && Math.abs(delta) < DRAG_THRESHOLD) return;

      if (!moved) {
        moved = true;
        this.setAttribute('dragging', '');
        document.addEventListener('click', preventDefault, { capture: true, once: true, signal });
      }

      scroller.scrollLeft = startScrollLeft - delta;
    };

    const onPointerUp = () => {
      controller.abort();
      this.removeAttribute('dragging');
      this.#updateArrowState();
    };

    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerUp, { signal });
    document.addEventListener('pointercancel', onPointerUp, { signal });
  };
}

if (!customElements.get('icon-bar-component')) {
  customElements.define('icon-bar-component', IconBarComponent);
}
